import { useState, useEffect, useCallback, useRef } from "react";
import "../App.css";
import { saveWmtPracticeResults } from "../service/api";
import PageWrapper from "./PageWrapper";

const WMTPractice = ({ userId, onNext }) => {
  const [step, setStep] = useState(0);
  const [gridSize, setGridSize] = useState([2, 2]);
  const [grid, setGrid] = useState([]);
  const [selectedTiles, setSelectedTiles] = useState(new Set());
  const [correctTiles, setCorrectTiles] = useState(new Set());
  const [isCorrect, setIsCorrect] = useState(null);
  const [startTime] = useState(Date.now());
  const saveInFlight = useRef(false);
  const [saveError, setSaveError] = useState("");

  const saveResults = useCallback(async () => {
    const endTime = Date.now();
    const duration = Math.floor((endTime - startTime) / 1000);

    await saveWmtPracticeResults({
      userId,
      wmtPracticeTimestamp: new Date(startTime).toISOString(),
      wmtPracticeElapsed: duration
    });
    console.log("✅ WMT Practice saved.");
  }, [userId, startTime]);

  const finishPractice = useCallback(async () => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setSaveError("");
    try {
      await saveResults();
      onNext();
    } catch (err) {
      console.error("❌ Error saving WMT Practice:", err);
      setSaveError("We could not save the practice step. Press the spacebar to try again.");
    } finally {
      saveInFlight.current = false;
    }
  }, [onNext, saveResults]);

  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (event.code === "Space") {
        event.preventDefault();

        if (step === 0) {
          setStep(1);
        } else if (step === 2) {
          const userCorrect =
            [...correctTiles].every((tile) => selectedTiles.has(tile)) &&
            selectedTiles.size === correctTiles.size;

          setIsCorrect(userCorrect);
          setStep(3);
        } else if (step === 3) {
          if (isCorrect) {
            if (gridSize[1] === 2) {
              setGridSize([2, 3]);
              setStep(1);
              setSelectedTiles(new Set());
            } else {
              setStep(4);
            }
          } else {
            setStep(1);
            setSelectedTiles(new Set());
          }
        } else if (step === 4) {
          await finishPractice();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [step, selectedTiles, gridSize, isCorrect, correctTiles, finishPractice]);

  useEffect(() => {
    if (step === 1) {
      generateGrid(gridSize[0], gridSize[1]);
      setTimeout(() => {
        setStep(2);
        setGrid(Array(gridSize[0]).fill().map(() => Array(gridSize[1]).fill("")));
      }, 2000);
    }
  }, [step, gridSize]);

  const generateGrid = (rows, cols) => {
    const newGrid = Array(rows).fill().map(() => Array(cols).fill("white"));
    const totalTiles = rows * cols;

    let blueTileCount = Math.ceil(totalTiles / 2);
    if (totalTiles % 2 === 0 && blueTileCount === totalTiles / 2) {
      blueTileCount += 1;
    }

    const blueTiles = new Set();
    while (blueTiles.size < blueTileCount) {
      const r = Math.floor(Math.random() * rows);
      const c = Math.floor(Math.random() * cols);
      blueTiles.add(`${r}-${c}`);
    }

    blueTiles.forEach((tile) => {
      const [r, c] = tile.split("-").map(Number);
      newGrid[r][c] = "blue";
    });

    setCorrectTiles(blueTiles);
    setGrid(newGrid);
  };

  const toggleTile = (row, col) => {
    const tileId = `${row}-${col}`;
    setSelectedTiles((prev) =>
      prev.has(tileId)
        ? new Set([...prev].filter((id) => id !== tileId))
        : new Set(prev).add(tileId)
    );
  };

  return (
    <PageWrapper maxWidth="900px">
    <div className="wmt-container" data-testid="wmt-practice" data-step={step}>
      {step === 0 && (
        <p className="submit-text">Press the <b>spacebar</b> to begin the practice round.</p>
      )}
      {step === 1 && (
        <div
          className="grid-container"
          data-testid="wmt-practice-pattern-grid"
          style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
        >
          {grid.map((row, rowIndex) =>
            row.map((color, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className="grid-tile"
                data-testid={`wmt-practice-pattern-tile-${rowIndex}-${colIndex}`}
                data-tile-id={`${rowIndex}-${colIndex}`}
                data-color={color || "white"}
                style={{ backgroundColor: color || "white" }}
              />
            ))
          )}
        </div>
      )}
      {step === 2 && (
        <>
          <p className="instruction-text">
            Try to replicate the pattern shown on the previous screen. Click tiles to select them.
          </p>
          <div
            className="grid-container"
            data-testid="wmt-practice-input-grid"
            style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
          >
            {grid.map((row, rowIndex) =>
              row.map((_, colIndex) => {
                const tileId = `${rowIndex}-${colIndex}`;
                return (
                  <div
                    key={tileId}
                    className={`grid-tile ${selectedTiles.has(tileId) ? "selected" : ""}`}
                    data-testid={`wmt-practice-input-tile-${tileId}`}
                    data-tile-id={tileId}
                    onClick={() => toggleTile(rowIndex, colIndex)}
                  />
                );
              })
            )}
          </div>
          <p className="submit-text">Press the <b>spacebar</b> to submit.</p>
        </>
      )}
      {step === 3 && (
        <>
          <p className="instruction-text" style={{ color: isCorrect ? "green" : "red" }}>
            {isCorrect ? "✅ Correct! Well done!" : "Unfortunately, you did not correctly mark all the squares."}
          </p>
          {!isCorrect && (
            <div className="comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Correct Pattern</th>
                    <th>Your Selection</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div
                        className="grid-container"
                        data-testid="wmt-practice-correct-grid"
                        style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
                      >
                        {Array.from({ length: gridSize[0] }).map((_, rowIndex) =>
                          Array.from({ length: gridSize[1] }).map((_, colIndex) => {
                            const tileId = `${rowIndex}-${colIndex}`;
                            return (
                              <div
                                key={tileId}
                                className={`grid-tile ${correctTiles.has(tileId) ? "selected" : ""}`}
                              />
                            );
                          })
                        )}
                      </div>
                    </td>
                    <td>
                      <div
                        className="grid-container"
                        data-testid="wmt-practice-selected-grid"
                        style={{ gridTemplateColumns: `repeat(${gridSize[1]}, 50px)` }}
                      >
                        {Array.from({ length: gridSize[0] }).map((_, rowIndex) =>
                          Array.from({ length: gridSize[1] }).map((_, colIndex) => {
                            const tileId = `${rowIndex}-${colIndex}`;
                            return (
                              <div
                                key={tileId}
                                className={`grid-tile ${selectedTiles.has(tileId) ? "selected" : ""}`}
                              />
                            );
                          })
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="submit-text">Press the <b>spacebar</b> to proceed.</p>
        </>
      )}
      {step === 4 && (
        <p className="submit-text">Training is complete. Press the <b>spacebar</b> to begin the main task.</p>
      )}
      {saveError && <p className="alert alert-error" role="alert">{saveError}</p>}
    </div>
    </PageWrapper>
  );
};

export default WMTPractice;
